import { z } from 'zod';

import { EligibilityFormCustomization } from '@/pages/Onboarding/OnboardingFlowContext';
import { MINIMUM_BIRTH_YEAR, ZIPCODE_VALIDATION_REGEX } from '@/utils/constants';
import { getAge, validateEmployeeId } from '@/utils/stringValidators';
import { getProductMinAge } from '@/utils/productHelpers';
import { parseDate } from '@/utils/stringUtils';

export type CoverageFieldsErrorMessages = {
  firstName: string;
  lastName: string;
  zipCode: string;
  dateOfBirth: string;
  employeeId: string;
};

// Shared by ConfirmDetails and CoverageCheckWithEmail - same fields and rules, different messages.
export const createCoverageFieldsSchema = (errorMessages: CoverageFieldsErrorMessages) => z.object({
  firstName: z.string({ required_error: errorMessages.firstName })
    .min(1, { message: errorMessages.firstName }),
  lastName: z.string({ required_error: errorMessages.lastName })
    .min(1, { message: errorMessages.lastName }),
  zipCode: z.union([
    z.literal(''),
    z.string({ message: errorMessages.zipCode })
      .regex(ZIPCODE_VALIDATION_REGEX, errorMessages.zipCode),
  ]).optional(),
  dateOfBirth: z.string({
    required_error: errorMessages.dateOfBirth,
    // 14 characters to account for '/' and spaces
  }).length(14, { message: errorMessages.dateOfBirth })
    .refine((dob) => {
      // check that it is parsable as a date
      const parsedDate = parseDate(dob);
      const date = new Date(parsedDate);
      const isValidDate = !Number.isNaN(date.getTime());

      if (!isValidDate) {
        return false;
      }

      return date.getFullYear() >= MINIMUM_BIRTH_YEAR
          && (getAge(dob) >= getProductMinAge('sleepio'));
    }, { message: errorMessages.dateOfBirth }),
  employeeId: z.union([
    z.literal(''),
    z.string({ required_error: errorMessages.employeeId }),
  ]).optional(),
});

// Shared by ConfirmDetails (control) and CoverageCheckWithEmail (variant) - zip/employee id are
// only required, and employee id only format-checked, when the org config says to show them.
export const applyCoverageFieldsRefinement = <Schema extends z.ZodTypeAny>(
  schema: Schema,
  formCustomization: EligibilityFormCustomization,
  errorMessages: Pick<CoverageFieldsErrorMessages, 'zipCode' | 'employeeId'>,
) => schema.superRefine((data: z.infer<Schema>, ctx: z.RefinementCtx) => {
    const {
      showZipCode, showEmployeeId, employeeIdValidationEnabled,
      employeeIdAllowedChars, employeeIdMinLength, employeeIdMaxLength, employeeIdValidationErrorMessage,
    } = formCustomization;

    if (showZipCode && !data.zipCode) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['zipCode'], message: errorMessages.zipCode });
    }

    if (!showEmployeeId) {
      return;
    }

    const employeeIdValue = (data.employeeId ?? '').trim();
    if (!employeeIdValue) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['employeeId'], message: errorMessages.employeeId });
      return;
    }

    if (employeeIdValidationEnabled) {
      const validation = validateEmployeeId(employeeIdValue, {
        validationEnabled: employeeIdValidationEnabled,
        allowedChars: employeeIdAllowedChars,
        minLength: employeeIdMinLength,
        maxLength: employeeIdMaxLength,
        errorMessage: employeeIdValidationErrorMessage,
      });
      if (!validation.isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['employeeId'],
          message: validation.errorMessage || errorMessages.employeeId,
        });
      }
    }
  });
